import * as React from 'react';
import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Select } from 'antd';
import styled from 'styled-components';

import { popupData, PopupNames } from 'core';
import { usePopupStateOperations } from 'core/hooks';
import { LOG_STEPS, LOG_LEVELS } from 'core/utils/zoomProcessingLog';
import { Heading } from 'atoms';

import { ProcessingLogTimeline } from '../ProcessingLogTimeline';

export interface IZoomProcessingLog {
  lessonId: string;
  title?: string;
}

/** Full processing journal of a lesson with step/level filters. */
export const ProcessingLogModal = memo(() => {
  const { t } = useTranslation();
  const {
    operations: { hidePopup },
  } = usePopupStateOperations();

  const data = popupData()[PopupNames.zoomProcessingLog];

  const [step, setStep] = useState<string>();
  const [level, setLevel] = useState<string>();

  const close = useCallback(() => {
    hidePopup(PopupNames.zoomProcessingLog);
  }, [hidePopup]);

  if (!data) {
    return null;
  }

  return (
    <Wrapper>
      <Heading>{data.title ?? t('pages.zoom.processingLog.fullLog')}</Heading>

      <Filters>
        <Select
          allowClear
          style={{ width: 220 }}
          placeholder={t('pages.zoom.processingLog.filterStep')}
          value={step}
          onChange={(value) => setStep(value)}
          options={LOG_STEPS.map((value) => ({
            value,
            label: t(`pages.zoom.processingLog.steps.${value}`),
          }))}
        />
        <Select
          allowClear
          style={{ width: 180 }}
          placeholder={t('pages.zoom.processingLog.filterLevel')}
          value={level}
          onChange={(value) => setLevel(value)}
          options={LOG_LEVELS.map((value) => ({
            value,
            label: t(`pages.zoom.processingLog.levels.${value}`),
          }))}
        />
      </Filters>

      <LogArea>
        <ProcessingLogTimeline
          lessonId={data.lessonId}
          step={step}
          level={level}
        />
      </LogArea>

      <Buttons>
        <CloseButton type="button" onClick={close}>
          {t('button.close')}
        </CloseButton>
      </Buttons>
    </Wrapper>
  );
});

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 32px;
  width: 640px;
  max-width: 90vw;
  box-sizing: border-box;
  background-color: var(--k-bg-ffffff);
  border-radius: 8px;
`;

const Filters = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin: 16px 0;
`;

const LogArea = styled.div`
  max-height: 50vh;
  overflow-y: auto;
  padding-right: 8px;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
`;

const CloseButton = styled.button`
  height: 36px;
  padding: 0 20px;
  border: 1px solid var(--k-border-0d53fc);
  border-radius: 6px;
  background: var(--k-bg-ffffff);
  color: var(--k-fg-0d53fc);
  font-size: 14px;
  cursor: pointer;
`;
